import { useCallback, useEffect, useState } from 'react'

export interface UseScopedSelectedFeedResult {
    selectedFeedUri: string | undefined
    setSelectedFeedUri: (uri: string | undefined) => void
    /** False until the persisted selection for the current storageKey has been read. */
    isHydrated: boolean
}

/**
 * localStorage-backed feed selection, keyed by an externally-resolved storage key.
 *
 * `storageKey === null` means "no resolvable scope yet" (e.g. Mastodon protocol selected
 * but no session resolved) — hydration stays pending and nothing is read or written, so
 * the legacy unscoped key is never touched as a fallback.
 *
 * Bundles the hydrated key and the selection into one state update (rather than two
 * separate useState calls) so there is no render where one has updated and the other
 * hasn't — the same class of race mobile's `useSelectedFeed` closes with `hydratedKey`.
 */
export function useScopedSelectedFeed(storageKey: string | null): UseScopedSelectedFeedResult {
    const [state, setState] = useState<{ key: string | null; uri: string | undefined }>(() => {
        if (storageKey === null) return { key: null, uri: undefined }
        try {
            return { key: storageKey, uri: localStorage.getItem(storageKey) ?? undefined }
        } catch {
            return { key: storageKey, uri: undefined }
        }
    })

    // Re-hydrate when the key changes after mount (e.g. a Mastodon session resolves
    // asynchronously, or the active account switches). The lazy initializer above only
    // covers the key available at first render.
    useEffect(() => {
        if (storageKey === state.key) return
        if (storageKey === null) {
            setState({ key: null, uri: undefined })
            return
        }
        try {
            setState({ key: storageKey, uri: localStorage.getItem(storageKey) ?? undefined })
        } catch {
            setState({ key: storageKey, uri: undefined })
        }
    }, [storageKey, state.key])

    const isHydrated = storageKey !== null && state.key === storageKey

    const setSelectedFeedUri = useCallback(
        (uri: string | undefined) => {
            setState(prev => ({ ...prev, uri }))
            if (storageKey === null) return
            try {
                if (uri) {
                    localStorage.setItem(storageKey, uri)
                } else {
                    localStorage.removeItem(storageKey)
                }
            } catch {
                /* localStorage unavailable */
            }
        },
        [storageKey]
    )

    return { selectedFeedUri: state.uri, setSelectedFeedUri, isHydrated }
}
